import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, RefreshCw, X, ChevronDown, Filter, LayoutGrid } from 'lucide-react';
import { Site } from '../types';
import { callSiteApi } from '../utils';
import { PostsList } from './PostsList';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface ContentTabProps {
  site: Site;
  onUpdateSite?: (updates: Partial<Site>) => void;
}

export function ContentTab({ site }: ContentTabProps) {
  const [activeTab, setActiveTab] = useState<string>('page');
  const [postTypes, setPostTypes] = useState<any[]>([
    { name: 'page', label: 'Pages', total_count: 0 },
    { name: 'post', label: 'Posts', total_count: 0 }
  ]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('any');
  const [showSearch, setShowSearch] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  const fetchPostTypes = async () => {
    try {
      const data = await callSiteApi(site.url, site.publicKey, site.secretKey, 'content/post-types');
      if (data && data.post_types) {
        // Sort built-in types first (page, post), then alphabetically
        const sorted = data.post_types.sort((a: any, b: any) => {
          if (a.name === 'page') return -1;
          if (b.name === 'page') return 1;
          if (a.name === 'post') return -1;
          if (b.name === 'post') return 1;
          return a.label.localeCompare(b.label);
        });
        setPostTypes(sorted);
        // Default to page or first available
        if (sorted.length > 0 && !sorted.find((t: any) => t.name === activeTab)) {
          setActiveTab(sorted[0].name);
        }
      }
    } catch (error) {
      console.error('Failed to fetch post types:', error);
    }
  };

  useEffect(() => {
    fetchPostTypes();
  }, [site.id]);

  useEffect(() => {
    if (showSearch) searchRef.current?.focus();
  }, [showSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusRef.current && !statusRef.current.contains(event.target as Node)) {
        setShowStatusMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Real refresh happens in PostsList which reacts to isRefreshing trigger,
    // so we just reset it shortly after
    setTimeout(() => {
      fetchPostTypes();
      setIsRefreshing(false);
    }, 1000);
  };

  const statuses = [
    { value: 'any', label: 'All Statuses' },
    { value: 'publish', label: 'Published' },
    { value: 'draft', label: 'Drafts' },
    { value: 'trash', label: 'Trash' },
    { value: 'pending', label: 'Pending' },
    { value: 'private', label: 'Private' }
  ];

  const activeStatusLabel = statuses.find(s => s.value === statusFilter)?.label || 'All Statuses';

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        {/* Dynamic Sub-Tab Selection */}
        <div className="flex p-1 bg-muted/50 rounded-xl w-fit overflow-x-auto hide-scrollbar">
          {postTypes.length === 0 ? (
            null
          ) : (
            postTypes.map((type) => {
              const isActive = activeTab === type.name;
              return (
                <button
                  key={type.name}
                  onClick={() => setActiveTab(type.name)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4 opacity-70" />
                  {type.label}
                  {type.total_count > 0 && (
                     <span className={`px-1.5 py-0.5 rounded text-[10px] ${isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground/70'}`}>
                       {type.total_count}
                     </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Dynamic Header Actions */}
        <div className="flex items-center gap-2">
          <div className="relative flex items-center h-10">
            <AnimatePresence initial={false}>
              {showSearch ? (
                <motion.div
                  key="search-box"
                  initial={{ width: 40, opacity: 0.6 }}
                  animate={{ width: 256, opacity: 1 }}
                  exit={{ width: 40, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  className="relative flex items-center h-10"
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search titles..."
                    onBlur={() => { if (!searchTerm) setShowSearch(false); }}
                    className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                  />
                  <button
                    onMouseDown={(e) => { e.preventDefault(); setShowSearch(false); setSearchTerm(''); }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  key="search-btn"
                  onClick={() => setShowSearch(true)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Search className="w-4 h-4" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          <div className="relative h-10" ref={statusRef}>
            <button
              onClick={() => setShowStatusMenu(!showStatusMenu)}
              className={`flex items-center space-x-2 px-4 h-full rounded-lg border transition-colors ${
                statusFilter !== 'any'
                  ? 'bg-background shadow-sm text-foreground border-transparent'
                  : 'bg-card border-border text-foreground hover:bg-muted'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium whitespace-nowrap">
                {activeStatusLabel}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showStatusMenu ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {showStatusMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[150px] p-1.5 z-40"
                >
                  {statuses.map((status) => (
                    <button
                      key={status.value}
                      onClick={() => { setStatusFilter(status.value); setShowStatusMenu(false); }}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${
                        statusFilter === status.value ? 'bg-background shadow-sm text-foreground' : 'text-foreground hover:bg-accent'
                      }`}
                    >
                      <span>{status.label}</span>
                      {statusFilter === status.value && <div className="w-2 h-2 bg-current rounded-full" />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={handleRefresh}
            className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeTab}-${isRefreshing}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab && (
            <PostsList 
              site={site} 
              postType={activeTab}
              searchTerm={searchTerm} 
              statusFilter={statusFilter} 
              refreshTrigger={isRefreshing}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
